import React, { useState } from "react";
import useShareUrlExtractor from "../hooks/useShareUrlExtractor";
import "./KaminariShareExtractor.css";

/**
 * Kaminari Share URL Extractor component
 * Specifically designed for extracting and copying shareUrl parameter values
 * from Kaminari share links
 */
const KaminariShareExtractor = () => {
  const [inputUrl, setInputUrl] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [copiedUrls, setCopiedUrls] = useState([]);

  const {
    url,
    updateUrl,
    extractedUrl,
    error,
    copyExtractedUrl,
    copyFullUrl,
    copyStatus,
    lastCopiedUrl,
    getUrlDisplayName,
  } = useShareUrlExtractor({
    paramName: "shareUrl",
  });

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    updateUrl(inputUrl);
  };

  // Handle extracting and copying
  const handleCopyExtracted = async () => {
    const success = await copyExtractedUrl();
    if (success && extractedUrl) {
      // Add to copy history
      setCopiedUrls((prev) => {
        // Remove duplicates and keep the list short
        const filtered = prev
          .filter((item) => item !== extractedUrl)
          .slice(0, 9);
        return [extractedUrl, ...filtered];
      });
    }
  };

  // Copy from history
  const copyFromHistory = async (urlToCopy) => {
    await updateUrl(urlToCopy);
    await copyExtractedUrl();
  };

  // Clear history
  const clearHistory = () => {
    setCopiedUrls([]);
    setShowHistory(false);
  };

  return (
    <div className="kaminari-extractor">
      <div className="kaminari-header">
        <h2>Kaminari Share URL Extractor</h2>
        <p className="description">
          Extract and copy just the destination URL from a Kaminari share link.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="url-form">
        <div className="input-container">
          <input
            type="text"
            value={inputUrl}
            onChange={(e) => setInputUrl(e.target.value)}
            placeholder="Paste a Kaminari share URL..."
            className="url-input"
          />
          <button type="submit" className="extract-button">
            Extract
          </button>
        </div>

        {url && (
          <div className="small-text">
            Analyzing: {url.length > 50 ? url.substring(0, 47) + "..." : url}
          </div>
        )}
      </form>

      <div
        className={`result-container ${error ? "has-error" : ""} ${
          extractedUrl ? "has-result" : ""
        }`}
      >
        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        {extractedUrl && (
          <>
            <div className="result-header">
              <span className="result-label">Extracted URL:</span>
              {copyStatus.copied && (
                <span className="copied-badge">Copied!</span>
              )}
            </div>

            <div className="extracted-url">{extractedUrl}</div>

            <div className="action-buttons">
              <button
                className="copy-button primary"
                onClick={handleCopyExtracted}
                disabled={copyStatus.copied}
              >
                {copyStatus.copied ? "Copied!" : "Copy URL"}
              </button>

              <button
                className="open-button"
                onClick={() => window.open(extractedUrl, "_blank")}
              >
                Open URL
              </button>

              <button className="copy-button secondary" onClick={copyFullUrl}>
                Copy Full Link
              </button>
            </div>
          </>
        )}

        {!extractedUrl && !error && url && (
          <div className="no-result">
            No shareUrl parameter found in the provided URL.
          </div>
        )}
      </div>

      {/* Copy history */}
      {copiedUrls.length > 0 && (
        <div className="history-section">
          <div
            className="history-header"
            onClick={() => setShowHistory(!showHistory)}
          >
            <h3>Recent Copies {showHistory ? "▼" : "▶"}</h3>
            {showHistory && (
              <button className="clear-history" onClick={clearHistory}>
                Clear History
              </button>
            )}
          </div>

          {showHistory && (
            <div className="history-list">
              {copiedUrls.map((copiedUrl, index) => (
                <div
                  key={index}
                  className="history-item"
                  onClick={() => copyFromHistory(copiedUrl)}
                >
                  <div className="history-url-name">
                    {getUrlDisplayName(copiedUrl)}
                  </div>
                  <div className="history-url">
                    {copiedUrl.length > 60
                      ? copiedUrl.substring(0, 57) + "..."
                      : copiedUrl}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="usage-info">
        <h3>How to Use</h3>
        <ol>
          <li>
            Paste a Kaminari share URL (like
            https://danials.org/share#data=...?shareUrl=...)
          </li>
          <li>Click "Extract" to analyze the URL</li>
          <li>Copy the extracted destination URL or open it directly</li>
        </ol>
        <p className="note">
          This tool works with any URL that has a <code>shareUrl</code>{" "}
          parameter, including URLs where the parameter appears after a hash
          fragment (#).
        </p>
      </div>
    </div>
  );
};

export default KaminariShareExtractor;
